/**
 * One page of a drawn score, with what is selected outlined on top of it.
 *
 * The component draws nothing itself. The engine's SVG goes in untouched and every mark on
 * top of it is a plain box measured from that SVG, which is what keeps the editor's
 * appearance the engraver's business and the editor's behaviour ours.
 *
 * Marks are drawn in an overlay rather than by styling the SVG, because the drawn elements
 * belong to the engine: it rewrites them on every render, a chord is several elements that
 * have to be outlined as one, and a mark has to be able to fall outside the thing it marks.
 * The overlay takes no pointer events, so a click always reaches the page underneath.
 *
 * Colours are CSS variables with defaults, so a host themes the editor from its own
 * stylesheet without this component growing a prop for every shade.
 *
 * The playhead line carries a `data-score-playhead` attribute, so a host that scrolls the
 * page to keep the music in view can find it without this component knowing it scrolls.
 */

import { useMemo, useRef } from "react";
import type { CSSProperties, PointerEvent, ReactElement, ReactNode } from "react";

import type { EventId } from "../document/ids";
import type { Score } from "../document/types";
import { padded, pointIn } from "../render/geometry";
import type { Box } from "../render/geometry";
import { eventBox, hitAt } from "../render/hit";
import type { Hit } from "../render/hit";
import type { Rendering } from "../render/render";
import { playheadBox } from "../render/playhead";
import type { PlayheadPosition } from "../render/playhead";
import { caretBox, drawnScore, slotHit } from "../render/slots";
import type { CaretPosition } from "../render/slots";
import type { Selection } from "../editor/selection";
import { useBarBoxes, useBoxes } from "./use-rendering";

export interface ScoreViewProps {
  readonly rendering: Rendering | null;
  /**
   * The document that was drawn.
   *
   * Given it, a click anywhere in a bar puts the caret at the nearest moment of the nearest
   * voice; without it, only a click on something drawn counts, which is the behaviour of a
   * viewer rather than of an editor.
   */
  readonly score?: Score;
  /** Where the caret stands, from {@link useEditor}'s entry state. */
  readonly caret?: CaretPosition | null;
  /** 1-based, as the engine numbers pages. */
  readonly page?: number;
  readonly selection?: Selection;
  /** Events sounding now, from {@link usePlayback}. */
  readonly sounding?: readonly EventId[];
  /**
   * Where the music is, drawn as a line across the bar: `momentAt` of the playback position.
   * Needs `score`, since the line is placed among the moments drawn in the bar.
   */
  readonly playhead?: PlayheadPosition | null;
  /** How many pixels away from a note head a click still counts as hitting it. */
  readonly radius?: number;
  /** Change this to have the page measured again, e.g. after the window is resized. */
  readonly revision?: number;
  onPick?(hit: Hit | null, extending: boolean): void;
  readonly className?: string;
  readonly style?: CSSProperties;
  /** Drawn in the overlay, above the page: a caret, a cursor, a comment pin. */
  readonly children?: ReactNode;
}

const PAGE: CSSProperties = { position: "relative" };

const OVERLAY: CSSProperties = { position: "absolute", inset: 0, pointerEvents: "none" };

const SELECTED = "var(--score-editor-selection, rgba(56, 132, 255, 0.28))";
const SOUNDING = "var(--score-editor-sounding, rgba(255, 176, 32, 0.32))";
const CARET = "var(--score-editor-caret, rgba(56, 132, 255, 0.9))";
const PLAYHEAD = "var(--score-editor-playhead, rgba(220, 38, 38, 0.85))";

function markStyle(box: Box, colour: string): CSSProperties {
  return {
    position: "absolute",
    left: box.x,
    top: box.y,
    width: box.width,
    height: box.height,
    borderRadius: 3,
    backgroundColor: colour,
  };
}

/** One box per event, a chord's note heads taken together, leaving out what is not drawn. */
function marks(
  rendering: Rendering,
  boxes: ReadonlyMap<string, Box>,
  events: Iterable<EventId>,
): readonly { readonly id: EventId; readonly box: Box }[] {
  const found: { id: EventId; box: Box }[] = [];
  for (const id of events) {
    const box = eventBox(rendering, boxes, id);
    if (box !== null) found.push({ id, box: padded(box, 2) });
  }
  return found;
}

/**
 * A page of a rendering.
 *
 * The SVG is inserted as markup because that is what an engine produces. It is the engine's
 * own output, not anything a user typed: text from the document reaches it through the
 * MusicXML exporter, which escapes it, and is drawn by the engine as glyphs.
 */
export function ScoreView({
  rendering,
  score,
  caret,
  page = 1,
  selection,
  sounding,
  playhead,
  radius = 6,
  revision = 0,
  onPick,
  className,
  style,
  children,
}: ScoreViewProps): ReactElement | null {
  const host = useRef<HTMLDivElement>(null);
  const boxes = useBoxes(host, rendering, page, revision);
  const barBoxes = useBarBoxes(host, rendering, page, revision);

  // The bars are read from the page once per measurement rather than per click: a click
  // must answer at once, and the walk is over the whole document.
  const bars = useMemo(
    () =>
      rendering === null || score === undefined
        ? []
        : drawnScore(score, rendering, boxes, barBoxes, page),
    [score, rendering, boxes, barBoxes, page],
  );

  // A drawn thing under the pointer wins; the rest of the bar is the editor's empty space,
  // and a click there means the moment nearest it rather than nothing at all.
  const pick = (event: PointerEvent<HTMLDivElement>): void => {
    const element = host.current;
    if (onPick === undefined || element === null) return;
    const point = pointIn(element, event.clientX, event.clientY);
    onPick(hitAt(boxes, point, radius) ?? slotHit(bars, point), event.shiftKey);
  };

  const standing = caret === undefined || caret === null ? null : caretBox(bars, caret);
  const now = playhead === undefined || playhead === null ? null : playheadBox(bars, playhead);

  const drawn = rendering?.pages.find((sheet) => sheet.index === page) ?? null;
  if (rendering === null || drawn === null) return null;

  return (
    <div ref={host} className={className} style={{ ...PAGE, ...style }} onPointerDown={pick}>
      <div dangerouslySetInnerHTML={{ __html: drawn.svg }} />
      <div style={OVERLAY}>
        {marks(rendering, boxes, sounding ?? []).map((mark) => (
          <div key={`sounding-${mark.id}`} style={markStyle(mark.box, SOUNDING)} />
        ))}
        {marks(rendering, boxes, selection?.events ?? []).map((mark) => (
          <div key={`selected-${mark.id}`} style={markStyle(mark.box, SELECTED)} />
        ))}
        {standing === null ? null : <div style={markStyle(standing, CARET)} />}
        {now === null ? null : (
          <div data-score-playhead="" style={{ ...markStyle(now, PLAYHEAD), borderRadius: 1 }} />
        )}
        {children}
      </div>
    </div>
  );
}
